import { type AudioBackend } from '../audio/AudioBackend';
import { DomInputBinding } from '../input/DomInputBinding';
import { type CanvasSizeSource } from '../rendering/CanvasSize';
import { type Renderer } from '../rendering/Renderer';
import { BehaviorSystem } from '../systems/BehaviorSystem';
import { CollisionSystem } from '../systems/CollisionSystem';
import { DebugSystem } from '../systems/DebugSystem';
import { InputSystem } from '../systems/InputSystem';
import { PointerInteractionSystem } from '../systems/PointerInteractionSystem';
import { RenderSystem } from '../systems/RenderSystem';
import { type Unsubscribe } from '../utils/Unsubscribe';
import { createEngineContext, type EngineContext } from './EngineContext';
import { type FrameScheduler, type VisibilitySource } from './FrameScheduler';
import { GameLoop } from './GameLoop';
import { SystemRegistry } from './SystemRegistry';

/** Construction options for {@link Engine}. */
export interface EngineOptions {
  /** Graphics backend the render system submits to. */
  readonly renderer: Renderer;
  /** Where the canvas' current size is read from; normally a `CanvasSurface`. */
  readonly surface: CanvasSizeSource;
  /**
   * Element pointer events are read from, normally `surface.canvas`. Omit to leave input unbound.
   *
   * Also the element `context.cursor` shows and hides the operating system cursor over, since that is
   * the same surface the pointer is being read from.
   */
  readonly pointerTarget?: HTMLElement | undefined;
  /** Where keyboard events are read from, normally `window`. Omit to leave the keyboard unbound. */
  readonly keyboardTarget?: EventTarget | undefined;
  /**
   * Element `context.fullscreen` puts on the whole screen, normally `document.documentElement`. Omit to
   * leave fullscreen unavailable.
   */
  readonly fullscreenTarget?: HTMLElement | undefined;
  /** Overrides the audio backend; used by tests and by hosts that own their own `AudioContext`. */
  readonly audioBackend?: AudioBackend | undefined;
  /** Overrides the frame delta ceiling. */
  readonly maxDeltaSeconds?: number | undefined;
  /** Overrides the frame source; used by tests to step frames by hand. */
  readonly scheduler?: FrameScheduler | undefined;
  /** Overrides the visibility source; used by tests to simulate a hidden tab. */
  readonly visibility?: VisibilitySource | undefined;
}

/**
 * The composition root: it owns the services, the system list and the loop.
 *
 * An engine instance is an ordinary object with no global state behind it, so a page can run two of
 * them and a test can build one, step it and throw it away with nothing to reset.
 *
 * The default systems are registered up front because a frame without them does nothing observable.
 * All of them can be removed through {@link Engine.systems} by a host that wants a different frame.
 *
 * The services themselves come from {@link createEngineContext}, so this class is left owning what is
 * genuinely its own: the system order, the DOM bindings and the loop.
 */
export class Engine {
  /** Services handed to scenes and reached by behaviors through their entity. */
  readonly context: EngineContext;
  /** The ordered system list. Add custom systems here. */
  readonly systems: SystemRegistry;

  readonly #loop: GameLoop;
  readonly #inputBinding: DomInputBinding | undefined;
  readonly #detachUnlockGesture: Unsubscribe | undefined;

  #disposed = false;

  constructor(options: EngineOptions) {
    this.context = createEngineContext({
      renderer: options.renderer,
      audioBackend: options.audioBackend,
      pointerTarget: options.pointerTarget,
      fullscreenTarget: options.fullscreenTarget,
    });

    const { input, audio, assets, pointerLock } = this.context;

    this.systems = new SystemRegistry(this.context);
    this.systems.add(new InputSystem(input));
    this.systems.add(new PointerInteractionSystem(input));
    this.systems.add(new BehaviorSystem());
    this.systems.add(new CollisionSystem());
    this.systems.add(new RenderSystem({ renderer: options.renderer, surface: options.surface, textures: assets }));
    this.systems.add(new DebugSystem());

    if (options.pointerTarget !== undefined) {
      this.#inputBinding = new DomInputBinding({
        input,
        pointerTarget: options.pointerTarget,
        keyboardTarget: options.keyboardTarget,
        pointerLock,
      });
      this.#detachUnlockGesture = audio.attachUnlockGesture(options.pointerTarget, options.keyboardTarget);
    }

    this.#loop = new GameLoop({
      onTick: (deltaSeconds) => {
        this.#tick(deltaSeconds);
      },
      maxDeltaSeconds: options.maxDeltaSeconds,
      scheduler: options.scheduler,
      visibility: options.visibility,
    });
  }

  /** Whether the loop has been started and not yet stopped. */
  get isRunning(): boolean {
    return this.#loop.isRunning;
  }

  /** Starts the frame loop. Frames without an active scene do nothing but advance the clock. */
  start(): void {
    this.#loop.start();
  }

  /** Stops the frame loop, leaving the scene and every service intact. */
  stop(): void {
    this.#loop.stop();
  }

  /** Stops the loop, then tears down input, the scene, the systems, audio, assets and the renderer. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;

    this.#loop.dispose();
    this.#detachUnlockGesture?.();
    this.#inputBinding?.dispose();
    this.context.pointerLock.dispose();
    this.context.fullscreen.dispose();
    this.context.scenes.deactivate();
    this.systems.clear();
    this.context.events.removeAllListeners();
    this.context.audio.dispose();
    this.context.assets.dispose();
    this.context.renderer.dispose();
  }

  #tick(deltaSeconds: number): void {
    this.context.time.advance(deltaSeconds);

    const scene = this.context.scenes.activeScene;
    if (scene === undefined) {
      return;
    }
    this.systems.update(scene, deltaSeconds);
  }
}
